import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { appState, deviceAliases } from '../core/state';
import { currentRole } from '../core/access';
import { farmDevices } from '../farms/farms-view';
import { makeFarmRow, createFarmFilterState, buildFarmFilter, applyFarmFilter } from '../core/farm-list';
import { isHostlessFarm } from '../core/farm-kind';
import { navigate } from '../core/router';
import { showAdbWhitelistModal } from './adb-whitelist-modal';
// key(farmId, udid) → live enable state, so the device rows can reflect on/off + port.
let stateByKey = new Map();
let proxyHost = '';
let farmsCache = [];
const key = (farmId, udid) => `${farmId}:${udid}`;
// The `adb connect` target: the explicitly configured public host when set, otherwise
// the host the admin is already viewing the panel on (the correct panel endpoint).
function displayHost() {
    return proxyHost || window.location.hostname;
}
function deviceLabel(udid) {
    const dev = appState.allDevices.get(udid);
    return deviceAliases.get(udid) || dev?.['ro.product.model'] || udid;
}
async function loadFarms() {
    try {
        // Only host (regular/VMOS) farms are actionable here: a host farm proxies its
        // USB/network devices. Aggregator farms open their adb-proxy port automatically
        // via the clicker runner (ensureAdbProxy), and the cloud-phone farms (Duo,
        // GeeLark) have no relay/agent and an adb-free image — none of them needs manual
        // remote-ADB, so every agent-less farm is excluded from the picker (see
        // renderFarmList).
        const res = await fetch('/api/farms');
        if (res.ok)
            farmsCache = await res.json();
    }
    catch {
        farmsCache = [];
    }
}
async function loadState() {
    try {
        const res = await fetch('/api/adb-proxy/state');
        if (!res.ok)
            return;
        const data = await res.json();
        proxyHost = data.host;
        stateByKey = new Map(data.devices.map((d) => [key(d.farmId, d.udid), d]));
    }
    catch { /* leave state as-is */ }
}
// ── Farm-list page (bare #!/adb) ──────────────────────────────────────────────
//
// Farm picker rendered with the same row markup/classes as the Farms and Clicker
// lists (status dot + flag + name + type/team badges). Clicking a row navigates to
// the dedicated device page for that farm.
export async function renderAdbProxy() {
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    pageTitle.textContent = t('adbproxy.title');
    content.innerHTML = `
        <div class="adb-page adb-page--full">
            <div class="users-toolbar">
                <button class="add-user-btn" id="adb-whitelist-btn">${t('adbproxy.ip_whitelist')}</button>
            </div>
            <p class="settings-hint" style="margin:0 4px 12px">
                ${t('adbproxy.page_hint', {
        command: `<code>adb connect ${esc(displayHost())}:&lt;port&gt;</code>`,
    })}
            </p>
            <div class="farm-filter" id="adb-filter"></div>
            <div class="clicker-farm-list" id="adb-farm-list"></div>
        </div>
    `;
    document.getElementById('adb-whitelist-btn').addEventListener('click', () => showAdbWhitelistModal());
    await loadFarms();
    renderFarmList();
}
function renderFarmList() {
    const listEl = document.getElementById('adb-farm-list');
    if (!listEl)
        return;
    // Cloud-phone farms (Duo, GeeLark) have no agent/relay and an adb-free image;
    // aggregator farms open their adb-proxy port automatically via the clicker runner.
    // None of them needs manual remote-ADB, so exclude every agent-less farm here.
    const farms = farmsCache.filter((f) => !isHostlessFarm(f.farm_kind));
    if (farms.length === 0) {
        listEl.innerHTML = `<p class="empty">${t('adbproxy.no_farms')}</p>`;
        return;
    }
    listEl.innerHTML = '';
    for (const f of farms) {
        listEl.appendChild(makeFarmRow(f, {
            online: f.online,
            meta: '',
            onClick: () => navigate({ page: 'adbProxy', farmId: f.id }),
        }));
    }
    applyFarmFilter(listEl, adbFilter);
    const filterEl = document.getElementById('adb-filter');
    if (filterEl)
        void buildFarmFilter(filterEl, adbFilter, farms, { showTeams: currentRole() === 'superadmin' }, () => applyFarmFilter(listEl, adbFilter));
}
// Client-side farm-list filter for the Remote ADB list. Module-level so the
// selection survives re-renders.
const adbFilter = createFarmFilterState();
// ── Device-list page (#!/adb/<farm>) ──────────────────────────────────────────
export async function renderAdbProxyDevices(farmId) {
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    pageTitle.textContent = t('adbproxy.title');
    content.innerHTML = `
        <div class="adb-page adb-page--full">
            <div class="farm-devs-header">
                <button class="back-btn" id="adb-back-btn">${t('adbproxy.back_farms')}</button>
                <h3 class="settings-section__title" id="adb-farm-title" style="margin:0"></h3>
            </div>
            <div class="adb-device-list" id="adb-device-list"></div>
        </div>
    `;
    document.getElementById('adb-back-btn').addEventListener('click', () => {
        navigate({ page: 'adbProxy' });
    });
    await loadFarms();
    await loadState();
    const titleEl = document.getElementById('adb-farm-title');
    if (titleEl)
        titleEl.textContent = farmsCache.find((f) => f.id === farmId)?.name ?? t('common.farm');
    renderDeviceList(farmId);
}
function renderDeviceList(farmId) {
    const listEl = document.getElementById('adb-device-list');
    if (!listEl)
        return;
    const farm = farmsCache.find((f) => f.id === farmId);
    if (!farm) {
        listEl.innerHTML = `<p class="empty">${t('adbproxy.farm_not_found')}</p>`;
        return;
    }
    if (isHostlessFarm(farm.farm_kind)) {
        listEl.innerHTML = `<p class="empty">${t('adbproxy.not_available_for_farm')}</p>`;
        return;
    }
    const devs = farmDevices(appState.allDevices, farm.id, farm.name);
    if (devs.length === 0) {
        listEl.innerHTML = `<p class="empty">${t('adbproxy.no_live_devices')}</p>`;
        return;
    }
    listEl.innerHTML = devs.map((d) => {
        const st = stateByKey.get(key(farm.id, d.udid));
        const on = !!st;
        const online = d.state === 'device';
        const command = `adb connect ${displayHost()}:${st?.port}`;
        const endpoint = on
            ? `<span class="adb-endpoint"><code>${esc(command)}</code>
                 <button class="tbl-btn adb-copy" data-endpoint="${esc(command)}" title="${t('common.copy')}">${t('common.copy')}</button></span>`
            : `<span class="clicker-device-row__meta">${t('common.off')}</span>`;
        const toggle = `<button class="modal__submit ${on ? 'modal__submit--danger' : ''} adb-toggle"
                data-udid="${esc(d.udid)}" data-on="${on ? '1' : '0'}">
            ${on ? t('adbproxy.disable') : t('adbproxy.enable')}
        </button>`;
        return `
            <div class="adb-device-row" data-udid="${esc(d.udid)}">
                <span class="status-dot ${online ? 'status-dot--green' : 'status-dot--red'}"></span>
                <span class="adb-device-name">
                    <span class="clicker-device-row__name" title="${esc(d.udid)}">${esc(deviceLabel(d.udid))}</span>
                    <span class="settings-hint"><code>${esc(d.udid)}</code></span>
                </span>
                <span class="adb-device-controls">
                    ${endpoint}
                    ${toggle}
                </span>
            </div>
        `;
    }).join('');
    listEl.querySelectorAll('.adb-copy').forEach((btn) => {
        btn.addEventListener('click', () => {
            void navigator.clipboard?.writeText(btn.dataset['endpoint'] ?? '');
            const prev = btn.textContent;
            btn.textContent = t('common.copied');
            setTimeout(() => { btn.textContent = prev; }, 1500);
        });
    });
    listEl.querySelectorAll('.adb-toggle').forEach((btn) => {
        btn.addEventListener('click', () => void toggleDevice(btn, farm.id, farm.name));
    });
}
async function toggleDevice(btn, farmId, _farmName) {
    const udid = btn.dataset['udid'] ?? '';
    const turnOn = btn.dataset['on'] !== '1';
    btn.disabled = true;
    const action = turnOn ? 'enable' : 'disable';
    try {
        const res = await fetch(`/api/adb-proxy/farms/${farmId}/devices/${encodeURIComponent(udid)}/${action}`, { method: 'POST' });
        if (!res.ok) {
            const err = (await res.json().catch(() => ({}))).error ?? t('adbproxy.request_failed');
            alert(err);
            btn.disabled = false;
            return;
        }
        await loadState();
        renderDeviceList(farmId);
    }
    catch {
        alert(t('adbproxy.network_error'));
        btn.disabled = false;
    }
}
